import { useCallback, useMemo, useState } from "react";
import { QUIZ_QUESTIONS } from "@/lib/quiz-config";
import type { QuizAnswers, QuizOption, QuizQuestion, QuizResolvable } from "@/types/quiz";

// ----------------------------------------------------------------------
// useQuiz — the branching quiz engine.
//
// Walks QUIZ_QUESTIONS in order and, given the answers collected so far,
// exposes only the questions that belong to the current path. A committed
// answer advances the flow; the "draft" holds the current selection until
// the user validates it (mirrors the "Valider ma réponse" UX). The UI stays
// purely presentational — all branching lives here and in the config.
// ----------------------------------------------------------------------

/** Resolve a static-or-derived config value against the current answers. */
const resolve = <T,>(value: QuizResolvable<T>, answers: QuizAnswers): T =>
  typeof value === "function" ? (value as (a: QuizAnswers) => T)(answers) : value;

/** A question with its title/options already resolved for the current answers. */
export interface ResolvedQuestion {
  question: QuizQuestion;
  title: string;
  options: QuizOption[];
}

export interface QuizEngine {
  answers: QuizAnswers;
  /** Questions on the current path, in order (grows/shrinks as branches resolve). */
  visibleQuestions: QuizQuestion[];
  /** The question awaiting an answer, resolved — or null once the flow is done. */
  current: ResolvedQuestion | null;
  /** Currently selected (but not yet validated) option value for `current`. */
  draft: string;
  /** 1-based position of `current` within the visible questions. */
  step: number;
  /** Number of questions on the current path. */
  total: number;
  /** True once every visible question has been answered. */
  isComplete: boolean;
  /** Select an option for the current question (does not advance). */
  select: (value: string) => void;
  /** Store an optional free-text value (keyed by the field id). */
  setFreeText: (fieldId: string, value: string) => void;
  /** Commit the current selection and advance to the next question. */
  validate: () => void;
  /** Clear every answer and restart the flow. */
  reset: () => void;
}

export function useQuiz(): QuizEngine {
  const [answers, setAnswers] = useState<QuizAnswers>({});
  const [draft, setDraft] = useState("");

  const visibleQuestions = useMemo(
    () => QUIZ_QUESTIONS.filter((q) => !q.when || q.when(answers)),
    [answers],
  );

  // First unanswered visible question; -1 → the flow is complete.
  const currentIndex = useMemo(
    () => visibleQuestions.findIndex((q) => !answers[q.id]),
    [visibleQuestions, answers],
  );
  const isComplete = currentIndex === -1;

  const current = useMemo<ResolvedQuestion | null>(() => {
    if (isComplete) return null;
    const question = visibleQuestions[currentIndex];
    return {
      question,
      title: resolve(question.title, answers),
      options: resolve(question.options, answers),
    };
  }, [visibleQuestions, currentIndex, isComplete, answers]);

  const total = visibleQuestions.length;
  const step = isComplete ? total : currentIndex + 1;

  const select = useCallback((value: string) => setDraft(value), []);

  const setFreeText = useCallback(
    (fieldId: string, value: string) =>
      setAnswers((prev) => ({ ...prev, [fieldId]: value })),
    [],
  );

  const validate = useCallback(() => {
    if (!current || !draft) return;
    setAnswers((prev) => ({ ...prev, [current.question.id]: draft }));
    setDraft("");
  }, [current, draft]);

  const reset = useCallback(() => {
    setAnswers({});
    setDraft("");
  }, []);

  return {
    answers,
    visibleQuestions,
    current,
    draft,
    step,
    total,
    isComplete,
    select,
    setFreeText,
    validate,
    reset,
  };
}
